import React, { FC, useState } from 'react';
import { Button, Stack, Box as Wrapper, Text, Icon, Input, TextLabel } from '@tonic-ui/react';
import { Modal } from '../../components/modal';
import { useTranslation } from 'react-i18next';

import { FormContainer, InputField, wsPasswordUpdateValidation } from '../../components/forms';
import NotificationBanner from '../../components/notification-banner/notification-banner';
import { updateWSPassword } from '../../services/ds-api';

type WSPasswordUpdateModalProps = {
  account: string;
  username: string;
  code: string;
  onClose: () => void;
  isOpen: boolean;
};

const WSPasswordUpdateModal: FC<WSPasswordUpdateModalProps> = ({
  account,
  username,
  code,
  onClose,
  isOpen,
}) => {
  const { t } = useTranslation();
  const [formErrors, setFormErrors] = useState({
    error: '',
  });

  const [passwordUpdateSuccessMsg, setPasswordUpdateSuccessMsg] = useState('');
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);

  const handleSubmit = async ({ newPassword, confirmPassword, setSubmitting, resetForm }) => {
    setDisableSubmitButton(true);
    setFormErrors({
      ...formErrors,
      error: '',
    });
    try {
      const { updatedUser, error, ...rest } = await updateWSPassword(
        newPassword,
        confirmPassword,
        code
      );
      if (error) {
        console.error(error);

        setFormErrors({
          ...formErrors,
          error: error,
        });
        setSubmitting(false);
        setDisableSubmitButton(false);
        resetForm();
      } else if (updatedUser?.state === 'UPDATED') {
        setPasswordUpdateSuccessMsg(t('wsPasswordUpdate.successMsg'));
      }
    } catch (error) {
      console.error(error);

      setFormErrors({
        ...formErrors,
        error: error.message ? error.message : error,
      });

      setSubmitting(false);
      setDisableSubmitButton(false);
      resetForm();
    }
  };

  return (
    <Modal
      id="ws-password-update"
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      header={t('wsPasswordUpdate.title')}
      body={
        <FormContainer
          id="ws-password-update-form"
          customValidation={wsPasswordUpdateValidation}
          initialValues={{
            newPassword: '',
            confirmPassword: '',
          }}
          submitAction={(formPayload) => handleSubmit(formPayload)}
        >
          {passwordUpdateSuccessMsg ? (
            <Stack direction="row" spacing="3x" margin="20px auto">
              <Icon icon="check-bold" color="#00a94f" />
              <Text>{passwordUpdateSuccessMsg}</Text>
            </Stack>
          ) : (
            <>
              {formErrors?.error && (
                <NotificationBanner
                  name="ws-forgot-password-form"
                  severity="error"
                  shouldOpen={true}
                  bannerText={formErrors.error}
                />
              )}
              <Text margin="15px 0">{t('wsPasswordUpdate.instruction')}</Text>
              <Wrapper maxWidth={360} minHeight={350}>
                <Stack spacing="4x">
                  <Wrapper>
                    <TextLabel mb="1x">{t('wsCommon.account')}:</TextLabel>
                    <Input readOnly defaultValue={account} />
                  </Wrapper>
                  <Wrapper>
                    <TextLabel mb="1x">{t('wsCommon.username')}:</TextLabel>
                    <Input readOnly defaultValue={username} />
                  </Wrapper>
                  <Wrapper>
                    <InputField
                      id="ws-new-password"
                      type="password"
                      label={t('wsPasswordUpdate.newPassword')}
                      name="newPassword"
                      isRequired={true}
                    />
                  </Wrapper>
                  <Wrapper>
                    <InputField
                      id="ws-confirm-password"
                      type="password"
                      label={t('confirmPassword')}
                      name="confirmPassword"
                      isRequired={true}
                    />
                  </Wrapper>
                </Stack>
              </Wrapper>
            </>
          )}
        </FormContainer>
      }
      footer={
        <>
          {!passwordUpdateSuccessMsg && (
            <Button
              form="c1c_form_ws-password-update-form"
              type="submit"
              variant="primary"
              data-id="ws-password-update-form-submit-btn"
              disabled={disableSubmitButton}
            >
              {t('wsCommon.ok')}
            </Button>
          )}
          <Button width="fit-content" marginLeft="4x" onClick={onClose}>
            {passwordUpdateSuccessMsg ? t('close') : t('cancel')}
          </Button>
        </>
      }
    />
  );
};

WSPasswordUpdateModal.displayName = 'WSPasswordUpdateModal';
export default WSPasswordUpdateModal;
